import { defineStore } from "pinia";
import { computed, ref, reactive } from "vue";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { initializeApp } from "firebase/app";
import { getFirestore, doc, getDoc, onSnapshot } from "firebase/firestore";
import FirebaseServices from "@/services/firebase.js";
import SubscriptionServices from "@/services/subscription.js";
import {
  categoriesList,
  getCreditsData,
  findCategoryFromSubCat,
  extractSubCategoryFromRoute,
} from "@/util/common.util";

const defaultColor = { hex: "#FFFFFF" };
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTHDOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECTID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGEBUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGINGSENDERID,
  appId: import.meta.env.VITE_FIREBASE_APPID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENTID,
};

initializeApp(firebaseConfig);

export const useMockupStore = defineStore("mockups", () => {
  const data = ref([]);
  const categoryFilter = ref([]);
  const subcategoryFilter = ref([]);
  const categorizedData = ref({});
  const categories = ref([]);
  const subcategories = ref([]);
  const color = ref(defaultColor);
  const bundle = ref({});
  const isSubscriber = ref(false);
  const isSubscriberLoaded = ref(false);
  const subscriptionId = ref(null);
  const subscriberType = ref("free");
  const credits = ref(null);
  const user = reactive({});
  const isUserLoaded = ref(false);
  const subscriptionData = ref(null);
  const currentRoute = ref("default");
  const isNewUser = ref(false);
  const pricing = ref(null);
  const aiAnimateLoader = ref(false);
  const featuredMockups = ref({});

  const categorizedDataComputed = computed(() => categorizedData.value);

  const filteredData = computed(() => {
    let categoryFilterFlat = categoryFilter.value.flat();
    let subcategoryFilterFlat = subcategoryFilter.value.flat();

    let filteredCatData = {};
    let filteredResult = [];

    if (!categoryFilterFlat.length) {
      filteredCatData = Object.keys(categorizedData.value).reduce(
        (acc, category) => {
          if (!categorizedData.value[category]) return acc;
          return { ...acc, ...categorizedData.value[category] };
        },
        {},
      );
    } else {
      filteredCatData = categoryFilterFlat.reduce((acc, category) => {
        if (!categorizedData.value[category]) return acc;
        return { ...acc, ...categorizedData.value[category] };
      }, {});
    }

    if (!subcategoryFilterFlat.length) {
      filteredResult = Object.keys(filteredCatData).reduce((acc, curr) => {
        if (!filteredCatData[curr]) return acc;
        return [...acc, ...filteredCatData[curr]];
      }, []);
    } else {
      filteredResult = subcategoryFilterFlat.reduce((acc, subcategory) => {
        if (!filteredCatData[subcategory]) return acc;
        return [...acc, ...filteredCatData[subcategory]];
      }, []);
    }

    return [...new Set(filteredResult)];
  });

  const getSubcategoryWithCategories = computed(() => {
    return Object.keys(categorizedData.value).reduce((acc, category) => {
      if (acc[category]) {
        return;
      } else {
        return {
          ...acc,
          [category]: Object.keys(categorizedData.value[category]),
        };
      }
    }, {});
  });

  const getSubscriptionData = computed(() => subscriptionData.value);

  const authUser = computed(() => user.value);
  const isAIAnimateLoading = computed(() => aiAnimateLoader.value);
  const currentCredits = computed(() => credits.value);

  const fetchData = async (pageName, params, skip = 0, limit) => {
    try {
      let jsonData = {};
      let mockupData;
      let allData = [];
      const priorityPages = ["category", "subcategory", "mockup-tag-list"];
      const ENV = import.meta.env.VITE_ENV;
      if (priorityPages.includes(pageName)) {
        if (pageName === "subcategory") {
          if (data.value?.[params.category]?.[params.subcategory]) return;
          if (ENV === "dev") {
            mockupData = await import(
              `@/data/dynamic_devMockupsData/${params.category}/${params.subcategory}.json`
            );
          } else {
            mockupData = await import(
              `@/data/dynamic_mockupsData/${params.category}/${params.subcategory}.json`
            );
          }
          const finalData = mockupData.default;
          jsonData[params.category] = {};
          jsonData[params.category][params.subcategory] = finalData;
          allData.push(...finalData);
        } else if (pageName === "mockup-tag-list") {
          const extractedSubcategory = extractSubCategoryFromRoute(params.tags);
          const extractedCategory = findCategoryFromSubCat(
            categoriesList,
            extractedSubcategory,
          );
          if (data.value?.[extractedCategory]?.[extractedSubcategory]) return;
          if (ENV === "dev") {
            mockupData = await import(
              `@/data/dynamic_devMockupsData/${extractedCategory}/${extractedSubcategory}.json`
            );
          } else {
            mockupData = await import(
              `@/data/dynamic_mockupsData/${extractedCategory}/${extractedSubcategory}.json`
            );
          }
          const finalData = mockupData.default;
          jsonData[extractedCategory] = {};
          jsonData[extractedCategory][extractedSubcategory] = finalData;
          allData.push(...finalData);
        } else if (pageName === "category") {
          if (categoriesList[params.category]) {
            for (let subcategory of categoriesList[params.category]) {
              if (data.value?.[params.category]?.[subcategory]) return;
              if (ENV === "dev") {
                mockupData = await import(
                  `@/data/dynamic_devMockupsData/${params.category}/${subcategory}.json`
                );
              } else {
                mockupData = await import(
                  `@/data/dynamic_mockupsData/${params.category}/${subcategory}.json`
                );
              }
              const finalData = mockupData.default;
              jsonData[params.category] = {};
              jsonData[params.category][subcategory] = finalData;
              allData.push(...finalData);
            }
          }
        }
      } else {
        for (let category in categoriesList) {
          jsonData[category] = {};
          for (let subcategory of categoriesList[category]) {
            if (ENV === "dev") {
              mockupData = await import(
                `@/data/dynamic_devMockupsData/${category}/${subcategory}.json`
              );
            } else {
              mockupData = await import(
                `@/data/dynamic_mockupsData/${category}/${subcategory}.json`
              );
            }
            const finalData = mockupData.default;
            jsonData[category][subcategory] = finalData;
            allData.push(...finalData);
          }
        }
      }
      data.value = allData.slice(skip, limit);
      processData(allData.slice(skip, limit));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const processData = (data) => {
    let bundleIdArr = [];
    let bundleResult = {};

    let categorizedDataResult = data.reduce((acc, curr) => {
      if (curr.meta && curr.meta.category && curr.meta.subcategories) {
        let item = curr;
        if (item?.meta?.types?.includes("featured")) {
          for (const subcat of curr.meta.subcategories) {
            if (!featuredMockups.value[subcat]) {
              featuredMockups.value[subcat] = [];
            }
            featuredMockups.value[subcat].push(item);
          }
        }

        let subcategories = curr.meta.subcategories.reduce(
          (acc, curr) => {
            if (acc[curr]) {
              if (item.bundleId) {
                if (!bundleResult[item.bundleId]) {
                  bundleResult[item.bundleId] = [];
                }
                if (bundleResult[item.bundleId]) {
                  bundleResult[item.bundleId].push(item.id);
                }
                if (bundleIdArr.includes(item.bundleId)) {
                  return acc;
                } else {
                  bundleIdArr.push(item.bundleId);
                  return { ...acc, [curr]: [...acc[curr], item] };
                }
              }
              return { ...acc, [curr]: [...acc[curr], item] };
            } else {
              if (item.bundleId) {
                if (!bundleResult[item.bundleId]) {
                  bundleResult[item.bundleId] = [];
                }
                if (bundleResult[item.bundleId]) {
                  bundleResult[item.bundleId].push(item.id);
                }
                if (bundleIdArr.includes(item.bundleId)) {
                  return acc;
                } else {
                  bundleIdArr.push(item.bundleId);
                  return { ...acc, [curr]: [item] };
                }
              }
              return { ...acc, [curr]: [item] };
            }
          },
          acc[curr.meta.category] ? acc[curr.meta.category] : {},
        );

        return { ...acc, [curr.meta.category]: subcategories };
      } else {
        return acc;
      }
    }, {});

    const categoryKeys = Object.keys(categoriesList);
    const subcategoryKeys = Object.values(categoriesList).flat();

    categorizedData.value = categorizedDataResult;
    categories.value = categoryKeys;
    subcategories.value = subcategoryKeys;
    bundle.value = bundleResult;
  };

  const addCategoryFilter = (filter) => {
    if (!categoryFilter.value.includes(filter)) {
      categoryFilter.value.push(filter);
    }
  };

  const removeCategoryFilter = (filter) => {
    categoryFilter.value = categoryFilter.value.filter((f) => f !== filter);
  };

  const updateCategoryFilter = (filter) => {
    categoryFilter.value = filter ? [filter] : [];
  };

  const resetCategoryFilter = () => {
    categoryFilter.value = [];
  };

  const addSubcategoryFilter = (filter) => {
    if (!subcategoryFilter.value.includes(filter)) {
      subcategoryFilter.value.push(filter);
    }
  };

  const removeSubcategoryFilter = (filter) => {
    subcategoryFilter.value = subcategoryFilter.value.filter(
      (f) => f !== filter,
    );
  };

  const updateSubcategoryFilter = (filter) => {
    subcategoryFilter.value = filter ? [filter] : [];
  };

  const resetSubcategoryFilter = () => {
    subcategoryFilter.value = [];
  };

  const updateColor = (newColor) => {
    color.value = newColor;
  };

  const updateCurrentRoute = (route) => {
    currentRoute.value = route;
  };

  const updateNewUser = (status) => {
    isNewUser.value = status;
  };

  const updatePricing = (data) => {
    pricing.value = data;
  };

  const resetLoadingState = () => {
    isSubscriberLoaded.value = false;
    isUserLoaded.value = false;
  };

  const getCredits = async (email) => {
    if (email) {
      const db = getFirestore();
      const docRef = doc(db, "Users", email);
      try {
        const unsubscribe = onSnapshot(docRef, (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            if (data) {
              credits.value = {
                credits: data?.credits,
                topup: getCreditsData(data) || 0,
              };
            }
          }
        });
        return unsubscribe;
      } catch (e) {
        console.error("Error setting up listener for credits:", e);
      }
    }
  };

  const initAuth = async () => {
    onAuthStateChanged(getAuth(), async (userData) => {
      if (userData) {
        // Check and delete AI-generated images from the database after 24 hours
        await FirebaseServices.checkAndDeleteInvalidOrExpiredUrls(
          userData.email,
        );
        await FirebaseServices.resetDownloadCount(userData.email);
        const userObj = {
          email: userData.email,
          displayName: userData.displayName,
          photoURL: userData.photoURL,
        };
        const db = getFirestore();
        const docRef = doc(db, "Users", userData.email);
        try {
          const docSnap = await getDoc(docRef);
          const data = docSnap.data();
          if (
            data &&
            data?.subscription_id &&
            typeof data?.subscription_id === "number"
          ) {
            isSubscriber.value = true;
            subscriptionId.value = data.subscription_id;
            const subscription = await SubscriptionServices.getSubscription(
              data.subscription_id,
              userObj.email,
            );
            const subscriptionDataResponse = await subscription?.json();
            subscriptionData.value = subscriptionDataResponse;
            if (data?.subscriber_type) {
              subscriberType.value = data?.subscriber_type;
            }
            if (
              data?.subscriber_type == "max-monthly" ||
              data?.subscriber_type == "max-yearly"
            ) {
              await getCredits(userObj.email);
            }
          } else {
            isSubscriber.value = false;
          }
        } catch (error) {
          console.log(error);
        } finally {
          user.value = userObj;
          isUserLoaded.value = true;
          isSubscriberLoaded.value = true;
        }
      } else {
        user.value = null;
        isUserLoaded.value = true;
        isSubscriberLoaded.value = true;
      }
    });
  };

  const updateAIAnimateLoader = (state) => {
    aiAnimateLoader.value = state;
  };

  return {
    // States
    data,
    categoryFilter,
    subcategoryFilter,
    categories,
    subcategories,
    color,
    bundle,
    isSubscriber,
    isSubscriberLoaded,
    subscriptionId,
    subscriberType,
    credits: currentCredits,
    authUser,
    isUserLoaded,
    subscriptionData,
    currentRoute,
    isNewUser,
    pricing,
    featuredMockups,
    // Getters
    categorizedData: categorizedDataComputed,
    filteredData,
    getSubcategoryWithCategories,
    getSubscriptionData,
    isAIAnimateLoading,
    // Actions
    fetchData,
    addCategoryFilter,
    removeCategoryFilter,
    updateCategoryFilter,
    resetCategoryFilter,
    addSubcategoryFilter,
    removeSubcategoryFilter,
    updateSubcategoryFilter,
    resetSubcategoryFilter,
    updateColor,
    updateCurrentRoute,
    initAuth,
    getCredits,
    resetLoadingState,
    updateNewUser,
    updatePricing,
    updateAIAnimateLoader,
  };
});
